import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './BannerManagement.css';

/**
 * BannerManagement Component
 * 
 * Gerencia banners do sistema com funcionalidades de:
 * - Criar novo banner com upload de imagem
 * - Editar banner existente (incluindo trocar imagem)
 * - Ativar/Desativar banners
 * - Reordenar banners
 * - Excluir banners
 * 
 * Estado editingBanner: null = modo criar | objeto = modo editar
 */
const BannerManagement = () => {
    const navigate = useNavigate();
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null); // null = criar | objeto = editar

    // Form state
    const [formData, setFormData] = useState({
        title: '',
        subtitle: '',
        linkUrl: '',
        order: 0
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        fetchBanners();
    }, []);

    const fetchBanners = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/banners`);
            setBanners(response.data);
        } catch (error) {
            console.error('Erro ao buscar banners:', error);
            setError('Erro ao carregar banners');
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validar tipo
            if (!file.type.startsWith('image/')) {
                setError('Apenas imagens são permitidas');
                return;
            }
            // Validar tamanho (5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError('Imagem muito grande. Máximo 5MB');
                return;
            }

            setSelectedFile(file);
            setError('');

            // Preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    /**
     * Inicia edição de um banner
     * Popula o formulário com os dados do banner selecionado
     */
    const handleEdit = (banner) => {
        setEditingBanner(banner);
        setFormData({
            title: banner.title,
            subtitle: banner.subtitle,
            linkUrl: banner.linkUrl || '',
            order: banner.order
        });
        setImagePreview(banner.imageUrl); // Mostrar imagem atual
        setSelectedFile(null); // Limpar arquivo selecionado
        setShowForm(true);
        setError('');
        setSuccess('');
    };

    /**
     * Cancela edição/criação e reseta o formulário
     */
    const handleCancel = () => {
        setShowForm(false);
        setEditingBanner(null);
        setFormData({ title: '', subtitle: '', linkUrl: '', order: 0 });
        setSelectedFile(null);
        setImagePreview(null);
        setError('');
    };

    /**
     * Submit do formulário - cria ou atualiza banner
     * Se editingBanner existe = UPDATE, senão = CREATE
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Validações
        // Título e Subtítulo agora são opcionais

        // Imagem obrigatória apenas ao criar (não ao editar)
        if (!editingBanner && !selectedFile) {
            setError('Selecione uma imagem');
            return;
        }

        setUploading(true);

        try {
            let imageUrl = editingBanner?.imageUrl; // Manter imagem atual se editando

            // Upload de nova imagem (se selecionada)
            if (selectedFile) {
                const uploadFormData = new FormData();
                uploadFormData.append('image', selectedFile);

                const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'https://api.otymafarma.com';
                const uploadResponse = await axios.post(
                    `${baseUrl}/api/upload/banner`,
                    uploadFormData,
                    {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    }
                );

                imageUrl = uploadResponse.data.imageUrl;
            }

            // Preparar dados do banner
            const bannerData = {
                ...formData,
                imageUrl,
                order: parseInt(formData.order) || 0
            };

            if (editingBanner) {
                // ATUALIZAR banner existente
                await axios.put(`${process.env.REACT_APP_API_URL}/banners/${editingBanner.id}`, bannerData);
                setSuccess('Banner atualizado com sucesso!');
            } else {
                // CRIAR novo banner
                await axios.post(`${process.env.REACT_APP_API_URL}/banners`, bannerData);
                setSuccess('Banner criado com sucesso!');
            }

            // Reset form
            handleCancel();

            // Refresh list
            fetchBanners();
        } catch (error) {
            console.error('Erro ao salvar banner:', error);
            setError(error.response?.data?.error || 'Erro ao salvar banner');
        } finally {
            setUploading(false);
        }
    };

    const handleToggleActive = async (bannerId, currentStatus) => {
        try {
            await axios.put(`${process.env.REACT_APP_API_URL}/banners/${bannerId}`, {
                isActive: !currentStatus
            });
            fetchBanners();
            setSuccess('Status atualizado!');
        } catch (error) {
            console.error('Erro ao atualizar status:', error);
            setError('Erro ao atualizar status');
        }
    };

    const handleDelete = async (bannerId) => {
        if (!window.confirm('Tem certeza que deseja excluir este banner?')) {
            return;
        }

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/banners/${bannerId}`);
            fetchBanners();
            setSuccess('Banner excluído com sucesso!');
        } catch (error) {
            console.error('Erro ao excluir banner:', error);
            setError('Erro ao excluir banner');
        }
    };

    const handleReorder = async (bannerId, newOrder) => {
        try {
            await axios.put(`${process.env.REACT_APP_API_URL}/banners/${bannerId}`, {
                order: newOrder
            });
            fetchBanners();
        } catch (error) {
            console.error('Erro ao reordenar:', error);
            setError('Erro ao reordenar banner');
        }
    };

    if (loading) {
        return <div className="loading">Carregando banners...</div>;
    }

    return (
        <div className="banner-management">
            <div className="header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <button
                        className="btn-back"
                        onClick={() => navigate('/dashboard')}
                        title="Voltar ao Dashboard"
                    >
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <h1>Gerenciar Banners</h1>
                </div>
                <button
                    className="btn-primary"
                    onClick={() => {
                        if (showForm && editingBanner) {
                            // Se está editando, cancela
                            handleCancel();
                        } else {
                            // Toggle form
                            setShowForm(!showForm);
                            if (showForm) handleCancel();
                        }
                    }}
                >
                    {showForm ? 'Cancelar' : '+ Novo Banner'}
                </button>
            </div>

            {error && <div className="alert alert-error">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            {showForm && (
                <div className="banner-form-card">
                    <h2>{editingBanner ? 'Editar Banner' : 'Criar Novo Banner'}</h2>
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Imagem do Banner {!editingBanner && '*'}</label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileSelect}
                                disabled={uploading}
                            />
                            {imagePreview && (
                                <div className="image-preview">
                                    <img src={imagePreview} alt="Preview" />
                                    {editingBanner && !selectedFile && (
                                        <small style={{ color: '#666', marginTop: '8px', display: 'block' }}>
                                            Imagem atual. Selecione uma nova para substituir.
                                        </small>
                                    )}
                                </div>
                            )}
                            <small>Máximo 5MB. Formatos: JPG, PNG, GIF</small>
                        </div>

                        <div className="form-group">
                            <label>Título</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                placeholder="Ex: Black Friday 2025"
                                maxLength={100}
                                disabled={uploading}
                            />
                        </div>

                        <div className="form-group">
                            <label>Subtítulo</label>
                            <textarea
                                name="subtitle"
                                value={formData.subtitle}
                                onChange={handleInputChange}
                                placeholder="Ex: Até 70% de desconto em eletrônicos"
                                maxLength={200}
                                rows={3}
                                disabled={uploading}
                            />
                        </div>

                        <div className="form-group">
                            <label>Link (opcional)</label>
                            <input
                                type="url"
                                name="linkUrl"
                                value={formData.linkUrl}
                                onChange={handleInputChange}
                                placeholder="https://otymafarma.com/ofertas/black-friday"
                                disabled={uploading}
                            />
                        </div>

                        <div className="form-group">
                            <label>Ordem de Exibição</label>
                            <input
                                type="number"
                                name="order"
                                value={formData.order}
                                onChange={handleInputChange}
                                min={0}
                                disabled={uploading}
                            />
                            <small>Menor número aparece primeiro</small>
                        </div>

                        <button
                            type="submit"
                            className="btn-primary btn-block"
                            disabled={uploading}
                        >
                            {uploading ? 'Salvando...' : (editingBanner ? 'Atualizar Banner' : 'Criar Banner')}
                        </button>
                    </form>
                </div>
            )}

            <div className="banners-list">
                <h2>Banners Cadastrados ({banners.length})</h2>

                {banners.length === 0 ? (
                    <div className="empty-state">
                        <p>Nenhum banner cadastrado ainda.</p>
                        <button
                            className="btn-primary"
                            onClick={() => setShowForm(true)}
                        >
                            Criar Primeiro Banner
                        </button>
                    </div>
                ) : (
                    <div className="banner-grid">
                        {banners.map((banner) => (
                            <div key={banner.id} className="banner-card">
                                <div className="banner-image">
                                    <img src={banner.imageUrl} alt={banner.title} />
                                    <span className={`badge ${banner.isActive ? 'badge-active' : 'badge-inactive'}`}>
                                        {banner.isActive ? 'Ativo' : 'Inativo'}
                                    </span>
                                </div>

                                <div className="banner-info">
                                    <h3>{banner.title}</h3>
                                    <p>{banner.subtitle}</p>
                                    {banner.linkUrl && (
                                        <a href={banner.linkUrl} target="_blank" rel="noopener noreferrer">
                                            <i className="fas fa-external-link-alt"></i> Ver Link
                                        </a>
                                    )}
                                    <div className="banner-meta">
                                        <span>Ordem: {banner.order}</span>
                                    </div>
                                </div>

                                <div className="banner-actions">
                                    <div className="order-controls">
                                        <button
                                            onClick={() => handleReorder(banner.id, banner.order - 1)}
                                            disabled={banner.order === 0}
                                            title="Mover para cima"
                                        >
                                            <i className="fas fa-arrow-up"></i>
                                        </button>
                                        <button
                                            onClick={() => handleReorder(banner.id, banner.order + 1)}
                                            title="Mover para baixo"
                                        >
                                            <i className="fas fa-arrow-down"></i>
                                        </button>
                                    </div>

                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={banner.isActive}
                                            onChange={() => handleToggleActive(banner.id, banner.isActive)}
                                        />
                                        <span className="slider"></span>
                                    </label>

                                    <button
                                        className="btn-edit"
                                        onClick={() => handleEdit(banner)}
                                        title="Editar banner"
                                    >
                                        <i className="fas fa-edit"></i> Editar
                                    </button>

                                    <button
                                        className="btn-danger"
                                        onClick={() => handleDelete(banner.id)}
                                    >
                                        <i className="fas fa-trash"></i> Excluir
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BannerManagement;
